import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../store'
import { EngagementEvents, logCrmEvent } from '../../utils/crmEvents'
import { InputModal } from '../InputModal'
import { Card } from '../Card'
import './dormancy-modal.scss'
import { kycServices } from '../../../services'
import { CheckboxInput } from '../Input'

interface DormancyDetails {
  dormancy_3_months_left?: boolean
  dormancy_1_week_left?: boolean
  dormancy_1_day_left?: boolean
  daysUntilDormant: number
}

const DormancyReminderModal = () => {
  const dispatch = useDispatch()
  const { authToken, accountStatus, userId } = useSelector((state: RootState) => state.investor)
  const [showModal, setShowModal] = useState(false)
  const [dormancyDetails, setDormancyDetails] = useState<DormancyDetails>()
  const [isChecked, setChecked] = useState<boolean>(false)

  useEffect(() => {
    const getInvestorDormancyReminderDetails = async () => {
      const dormancyDetails = await kycServices.getInvestorDormancyReminderDetails({
        Authorization: authToken,
      })
      if (dormancyDetails?.status === 200) {
        setDormancyDetails(dormancyDetails.data)
      }
    }
    if (authToken && accountStatus !== 'DORMANT') {
      getInvestorDormancyReminderDetails()
    }
  }, [authToken, dispatch])

  useEffect(() => {
    ;(async () => {
      if (!authToken) return

      const response = await kycServices.getKYC_v1(userId?.toString(), {
        Authorization: authToken ? authToken : '',
      })
      if (!!response && response?.data && response?.status === 200) {
        if (
          dormancyDetails?.dormancy_3_months_left &&
          response?.data?.metadata?.accepted3monthReminder
        ) {
          setChecked(true)
        } else if (
          dormancyDetails?.dormancy_1_week_left &&
          response?.data?.metadata?.accepted1weekReminder
        ) {
          setChecked(true)
        } else if (
          dormancyDetails?.dormancy_1_day_left &&
          response?.data?.metadata?.accepted1dayReminder
        ) {
          setChecked(true)
        } else if (dormancyDetails && dormancyDetails?.daysUntilDormant > 0 && !isChecked) {
          setShowModal(true)
        }
      }
    })()
  }, [isChecked, dormancyDetails])

  const handleCheckboxClick = async () => {
    const payload = {
      declaration: {
        ...(dormancyDetails?.dormancy_3_months_left && { accepted3monthReminder: true }),
        ...(dormancyDetails?.dormancy_1_week_left && { accepted1weekReminder: true }),
        ...(dormancyDetails?.dormancy_1_day_left && { accepted1dayReminder: true }),
      },
    }
    const data = await kycServices.acceptInvestorDormancyReminder(payload, {
      Authorization: authToken ? authToken : '',
    })

    if (data && data.data && data.status === 200) {
      setChecked(!isChecked)

      logCrmEvent(
        EngagementEvents.VIEWED_ACCEPTED_DORMANCY_REMINDER,
        {
          Authorization: authToken ?? '',
        },
        {
          userId,
          daysLeftToDormancy: dormancyDetails?.daysUntilDormant,
        },
      )

      setTimeout(() => {
        setShowModal(false)
      }, 1000)
    }
  }

  const getReminderModalText = () => {
    if (dormancyDetails?.dormancy_3_months_left) {
      return (
        <div>
          This is a friendly reminder that your account has been inactive for{' '}
          <strong>3 months</strong>. You now have only{' '}
          <strong>{dormancyDetails?.daysUntilDormant} days</strong> left before your account is
          considered dormant and will be closed automatically if no investment is made.
          <br />
          <br />
          Key Points to Remember:
          <ul className='ml-32'>
            <li>
              <strong>No Investment:</strong> Your account will be considered dormant.
            </li>
            <li>
              <strong>Account Closure:</strong> Without any activity for 6 months, your account will
              be closed.
            </li>
          </ul>
          <br />
          Please make sure to take action before that to avoid account closure.
        </div>
      )
    }
    if (dormancyDetails?.dormancy_1_week_left) {
      return (
        <div>
          This is a friendly reminder that that you have only{' '}
          <strong>{dormancyDetails?.daysUntilDormant} days</strong> left before your account is
          considered dormant and will be closed automatically if no investment is made.
          <br />
          <br />
          Key Points to Remember:
          <ul className='ml-32'>
            <li>
              <strong>No Investment:</strong> Your account will be considered dormant.
            </li>
            <li>
              <strong>Account Closure:</strong> Without any activity for 6 months, your account will
              be closed.
            </li>
          </ul>
          <br />
          Please make sure to take action before that to avoid account closure.
        </div>
      )
    }
    if (dormancyDetails?.dormancy_1_day_left) {
      return (
        <div>
          This is your final reminder that you have <strong>only 1 day</strong> left before your
          account is automatically marked as dormant.
          <br />
          <br />
          If no investment is made, your account will be closed, and all related data will be lost.
          <br />
          <br />
          To avoid this, please log in and make an investment today!
        </div>
      )
    }
  }

  return showModal ? (
    <InputModal testId='dormancy-reminder-modal'>
      <div data-testid='dormancy-reminder-modal-card-container' className='modal-card-container'>
        <Card classes={['p-24 dormancy-acknowledgement-modal']}>
          <div
            className='self-declaration-wrapper self-declaration-wrapper-overflow mb-24'
            style={{ maxHeight: 'calc(100% - 110px)' }}
          >
            <div className='self-declaration-wrapper-text'>
              <h1 className='text-align-center mb-24' style={{ lineHeight: '120%' }}>
                Your Account is Going to be Marked as Dormant
              </h1>
              {getReminderModalText()}
            </div>
          </div>
          <div className='flex gap-12'>
            <CheckboxInput
              labelText='I Understand'
              name='acceptDormancyReminder'
              checked={isChecked}
              disabled={isChecked}
              value={isChecked ? 'true' : 'false'}
              handleChange={handleCheckboxClick}
              dataTestId='accept-dormancy-reminder'
            />
          </div>
        </Card>
      </div>
    </InputModal>
  ) : null
}
export { DormancyReminderModal }
